// src/shared/ui/Skeleton.jsx
//
// Pulsing placeholder blocks for content that's still loading. Replaces the
// bare <Spinner/> (or nothing) most pages showed while a fetch was in
// flight with a placeholder shaped like the content about to appear, so the
// page doesn't jump once data arrives. `Skeleton` is the raw block; the
// named exports below compose it into the shapes that show up repeatedly
// across modules (a table's rows, a card grid, a list of items, a block of
// text/detail fields).
//
// Usage:
//   <Skeleton className="h-4 w-32" />
//   <SkeletonText lines={3} />
//   <SkeletonTable rows={5} columns={4} />
//   <SkeletonList rows={4} />
//   <SkeletonCard />
//   <SkeletonCardGrid cards={3} />

import PropTypes from "prop-types";

export default function Skeleton({ className = "", style }) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded bg-gray-200 ${className}`.trim()}
      style={style}
    />
  );
}

Skeleton.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
};

export function SkeletonText({ lines = 3, className = "" }) {
  return (
    <div className={`flex flex-col gap-2 ${className}`.trim()} role="status" aria-label="Loading">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 && lines > 1 ? "w-2/3" : "w-full"}`} />
      ))}
    </div>
  );
}

SkeletonText.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string,
};

export function SkeletonCircle({ size = 40, className = "" }) {
  return <Skeleton className={`rounded-full shrink-0 ${className}`.trim()} style={{ width: size, height: size }} />;
}

SkeletonCircle.propTypes = {
  size: PropTypes.number,
  className: PropTypes.string,
};

// A single card-shaped placeholder — a title bar plus a couple of text
// lines. Matches the shape of a StatTile / SurfaceCard tile.
export function SkeletonCard({ className = "" }) {
  return (
    <div
      className={`rounded-card border border-line bg-white p-4 ${className}`.trim()}
      role="status"
      aria-label="Loading"
    >
      <Skeleton className="h-4 w-1/2 mb-3" />
      <SkeletonText lines={2} />
    </div>
  );
}

SkeletonCard.propTypes = { className: PropTypes.string };

// A responsive grid of SkeletonCards — for KPI/StatTile rows and card-grid
// pages (galleries, dashboards) while their data loads.
export function SkeletonCardGrid({ cards = 3, className = "" }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${className}`.trim()}>
      {Array.from({ length: cards }, (_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

SkeletonCardGrid.propTypes = { cards: PropTypes.number, className: PropTypes.string };

// Mimics a DataTable's row layout: `columns` skeleton bars per row, the
// first wider than the rest (matching a typical name/description-first
// column), so it sits in the same footprint as the real table once it
// renders. Use inside the same container that would otherwise hold
// <DataTable/> while `loading` is true.
export function SkeletonTable({ rows = 5, columns = 4, className = "" }) {
  return (
    <div className={`w-full ${className}`.trim()} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className="flex items-center gap-4 px-6 py-4 border-b border-gray-100 last:border-b-0"
        >
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className={`h-3 flex-1 ${c === 0 ? "max-w-[35%]" : "max-w-[18%]"}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

SkeletonTable.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
  className: PropTypes.string,
};

// A vertical list of icon/avatar + text rows — history feeds, chat
// transcripts, notification/activity lists.
export function SkeletonList({ rows = 4, className = "" }) {
  return (
    <div className={`flex flex-col gap-4 ${className}`.trim()} role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <SkeletonCircle size={32} />
          <div className="flex-1">
            <SkeletonText lines={2} />
          </div>
        </div>
      ))}
    </div>
  );
}

SkeletonList.propTypes = { rows: PropTypes.number, className: PropTypes.string };
